import React, { useState, useEffect, useCallback } from 'react';
import {
  Product,
  ProductCreate,
  ProductUpdate,
  VendorLookupItem,
  FamilyLookupItem,
  SubFamilyLookupItem,
  BarcodeType1LookupItem,
  BarcodeType2LookupItem,
  CurrencyLookupItem,
  UnitOfMeasureLookupItem,
} from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Spinner } from './ui/spinner.tsx'; // Import Spinner
import { Switch } from './ui/switch.tsx'; // NEW: Import Switch

interface ProductFormProps {
  initialData?: Product | null;
  // Modified onSubmit to accept isComposite flag and return ID for new composite
  onSubmit: (data: ProductCreate | ProductUpdate, isComposite: boolean) => Promise<boolean | number>;
  onCancel: () => void;
  isSubmitting?: boolean;
  vendors: VendorLookupItem[];
  families: FamilyLookupItem[];
  subFamilies: SubFamilyLookupItem[];
  barcodeType1s: BarcodeType1LookupItem[];
  barcodeType2s: BarcodeType2LookupItem[];
  currencies: CurrencyLookupItem[];
  unitsOfMeasure: UnitOfMeasureLookupItem[];
  lookupLoading: boolean;
  createComposite?: boolean;
}

const defaultFormData: ProductCreate = {
  vendor_id: null as any, // Will be deleted for composites
  family_id: null as any,
  article_number: '', // Changed from 0 to ''
  description: '',
  cost: 0.0, // Required for base products
  purchase_quantity: 1.0,
  currency_id: null as any, // Will be deleted for composites
  unit_of_measure_id: null, // Required for base products, null for composites
  markup_perc_wholesale: 0.0,
  markup_perc_retail: 0.0,
  created_by: 'tester',
  subfamily_id: null,
  barcode_1_id: null,
  barcode_2_id: null,
};

const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  vendors,
  families,
  subFamilies,
  barcodeType1s,
  barcodeType2s,
  currencies,
  unitsOfMeasure,
  lookupLoading,
  createComposite,
}) => {
  const [formData, setFormData] = useState<ProductCreate | ProductUpdate>(
    initialData
      ? {
          vendor_id: initialData.vendor_id || null,
          family_id: initialData.family_id || null,
          subfamily_id: initialData.subfamily_id || null,
          article_number: initialData.article_number,
          barcode_1_id: initialData.barcode_1_id || null,
          barcode_2_id: initialData.barcode_2_id || null,
          description: initialData.description,
          cost: initialData.cost,
          purchase_quantity: initialData.purchase_quantity,
          currency_id: initialData.currency_id || null,
          unit_of_measure_id: initialData.unit_of_measure_id || null,
          markup_perc_wholesale: initialData.markup_perc_wholesale,
          markup_perc_retail: initialData.markup_perc_retail,
          updated_by: 'tester',
        }
      : defaultFormData
  );

  // NEW: State for the switch
  const [isCreatingComposite, setIsCreatingComposite] = useState(false);
  // NEW: State for validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        vendor_id: initialData.vendor_id || null,
        family_id: initialData.family_id || null,
        subfamily_id: initialData.subfamily_id || null,
        article_number: initialData.article_number,
        barcode_1_id: initialData.barcode_1_id || null,
        barcode_2_id: initialData.barcode_2_id || null,
        description: initialData.description,
        cost: initialData.cost,
        purchase_quantity: initialData.purchase_quantity,
        currency_id: initialData.currency_id || null,
        unit_of_measure_id: initialData.unit_of_measure_id || null,
        markup_perc_wholesale: initialData.markup_perc_wholesale,
        markup_perc_retail: initialData.markup_perc_retail,
        updated_by: initialData.updated_by || 'tester',
      });
      setIsCreatingComposite(false); // Always false when editing an existing product
    } else {
      setFormData(defaultFormData);
      setIsCreatingComposite(false || createComposite); // Default to standard product for new creation
    }
    setValidationErrors({}); // Clear validation errors on initialData change
  }, [initialData, createComposite]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { id, value, type } = e.target;
      setFormData((prev) => ({
        ...prev,
        [id]: (id === 'article_number') ? value : (type === 'number' ? parseFloat(value) || 0 : value), // Handle article_number as string
      }));
      setValidationErrors((prev) => ({ ...prev, [id]: '' })); // Clear error on change
    },
    []
  );

  const handleSelectChange = useCallback(
    (id: keyof (ProductCreate | ProductUpdate), value: string) => {
      setFormData((prev) => ({
        ...prev,
        [id]: value === '' || value === '-' || value.startsWith('nil') ? null : parseInt(value, 10),
      }));
      setValidationErrors((prev) => ({ ...prev, [id]: '' })); // Clear error on change
    },
    []
  );

  // NEW: Consolidated submit handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setValidationErrors({}); // Clear previous errors

      let currentErrors: Record<string, string> = {};
      let payload: ProductCreate | ProductUpdate;

      if (isCreatingComposite) {
        // For composite, filter out specific fields and set unit_of_measure_id to null
        const compositePayload: ProductCreate = {
          ...(formData as ProductCreate), // Start with all current form data
          unit_of_measure_id: null, // Explicitly set to null for composites
        };
        // Delete fields that are not relevant for composite creation/update
        delete compositePayload.vendor_id;
        delete compositePayload.cost;
        delete compositePayload.currency_id;
        // Note: tax_profile_id, markup_perc_wholesale, markup_perc_retail are still relevant for composites
        payload = compositePayload;
      } else {
        // For standard product, perform validation
        if (formData.cost === null || formData.cost === undefined || formData.cost <= 0) {
          currentErrors.cost = 'Cost is required and must be greater than 0 for base products.';
        }
        if (formData.unit_of_measure_id === null || formData.unit_of_measure_id === undefined) {
          currentErrors.unit_of_measure_id = 'Unit of Measure is required for base products.';
        }
        if (formData.vendor_id === null || formData.vendor_id === undefined) {
          currentErrors.vendor_id = 'Vendor is required for base products.';
        }
        if (formData.currency_id === null || formData.currency_id === undefined) {
          currentErrors.currency_id = 'Currency is required for base products.';
        }
        payload = formData;
      }

      // Common validation for both types
      if (formData.family_id === null || formData.family_id === undefined) {
        currentErrors.family_id = 'Family is required.';
      }
      if (!formData.description || formData.description.trim() === '') {
        currentErrors.description = 'Description is required.';
      }
      if (!formData.article_number || String(formData.article_number).trim() === '') { // Updated validation for string
        currentErrors.article_number = 'Article Number is required.';
      }
      if (formData.markup_perc_wholesale === null || formData.markup_perc_wholesale === undefined) {
        currentErrors.markup_perc_wholesale = 'Wholesale Markup is required.';
      }
      if (formData.markup_perc_retail === null || formData.markup_perc_retail === undefined) {
        currentErrors.markup_perc_retail = 'Retail Markup is required.';
      }


      if (Object.keys(currentErrors).length > 0) {
        setValidationErrors(currentErrors);
        return; // Stop submission if validation fails
      }

      await onSubmit(payload, isCreatingComposite);
    },
    [formData, onSubmit, isCreatingComposite]
  );

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* NEW: Switch for product type, only visible for new products */}
      {!initialData && (
        <div className="flex items-center space-x-2 mb-4 p-2 border rounded-md bg-gray-50">
          <Label htmlFor="product-type-switch" className="text-sm font-medium">
            Create as Composite Product
          </Label>
          <Switch
            id="product-type-switch"
            checked={isCreatingComposite}
            onCheckedChange={setIsCreatingComposite}
            disabled={isDisabled}
          />
          <span className="text-sm text-muted-foreground">
            {isCreatingComposite ? 'Composite' : 'Standard'}
          </span>
        </div>
      )}

      <div>
        <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
          Description
        </Label>
        <Input
          id="description"
          type="text"
          required
          value={formData.description || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
        {validationErrors.description && <p className="text-destructive text-sm mt-1">{validationErrors.description}</p>}
      </div>

      <div>
        <Label htmlFor="article_number" className="block text-sm font-medium text-foreground mb-1">
          Article Number
        </Label>
        <Input
          id="article_number"
          type="text" // Changed to type="text"
          required
          value={formData.article_number || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
        {validationErrors.article_number && <p className="text-destructive text-sm mt-1">{validationErrors.article_number}</p>}
      </div>

      {/* Conditionally hidden fields for composite creation */}
      {!isCreatingComposite && (
        <>
          <div>
            <Label htmlFor="vendor_id" className="block text-sm font-medium text-foreground mb-1">
              Vendor
            </Label>
            <Select
              onValueChange={(value) => handleSelectChange('vendor_id', value)}
              value={formData.vendor_id ? String(formData.vendor_id) : ''}
              disabled={isDisabled}
              required={!isCreatingComposite}
            >
              <SelectTrigger id="vendor_id">
                <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading vendors...</span> : 'Select a vendor'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nil1" disabled>
                  {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading vendors...</span> : 'Select a vendor'}
                </SelectItem>
                {vendors.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validationErrors.vendor_id && <p className="text-destructive text-sm mt-1">{validationErrors.vendor_id}</p>}
          </div>
        </>
      )}

      <div>
        <Label htmlFor="family_id" className="block text-sm font-medium text-foreground mb-1">
          Family
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('family_id', value)}
          value={formData.family_id ? String(formData.family_id) : ''}
          disabled={isDisabled}
          required
        >
          <SelectTrigger id="family_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nil2" disabled>
              {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'}
            </SelectItem>
            {families.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {validationErrors.family_id && <p className="text-destructive text-sm mt-1">{validationErrors.family_id}</p>}
      </div>

      <div>
        <Label htmlFor="subfamily_id" className="block text-sm font-medium text-foreground mb-1">
          Subfamily (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('subfamily_id', value)}
          value={formData.subfamily_id ? String(formData.subfamily_id) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="subfamily_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading subfamilies...</span> : 'Select a subfamily (optional)'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {subFamilies.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="barcode_1_id" className="block text-sm font-medium text-foreground mb-1">
          Barcode Type 1 (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('barcode_1_id', value)}
          value={formData.barcode_1_id ? String(formData.barcode_1_id) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="barcode_1_id">
            <SelectValue
              placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 1 (optional)'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {barcodeType1s.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="barcode_2_id" className="block text-sm font-medium text-foreground mb-1">
          Barcode Type 2 (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('barcode_2_id', value)}
          value={formData.barcode_2_id ? String(formData.barcode_2_id) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="barcode_2_id">
            <SelectValue
              placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 2 (optional)'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {barcodeType2s.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conditionally hidden fields for composite creation */}
      {!isCreatingComposite && (
        <>
          <div>
            <Label htmlFor="cost" className="block text-sm font-medium text-foreground mb-1">
              Cost
            </Label>
            <Input
              id="cost"
              type="number"
              step="0.01"
              required={!isCreatingComposite}
              value={formData.cost || ''}
              onChange={handleChange}
              disabled={isDisabled}
            />
            {validationErrors.cost && <p className="text-destructive text-sm mt-1">{validationErrors.cost}</p>}
          </div>

          <div>
            <Label htmlFor="purchase_quantity" className="block text-sm font-medium text-foreground mb-1">
              Purchase Quantity
            </Label>
            <Input
              id="purchase_quantity"
              type="number"
              step="0.000001"
              min="0.000001"
              required
              value={formData.purchase_quantity ?? ''}
              onChange={handleChange}
              disabled={isDisabled}
            />
          </div>

          <div>
            <Label htmlFor="currency_id" className="block text-sm font-medium text-foreground mb-1">
              Currency
            </Label>
            <Select
              onValueChange={(value) => handleSelectChange('currency_id', value)}
              value={formData.currency_id ? String(formData.currency_id) : ''}
              disabled={isDisabled}
              required={!isCreatingComposite}
            >
              <SelectTrigger id="currency_id">
                <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nil3" disabled>
                  {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'}
                </SelectItem>
                {currencies.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validationErrors.currency_id && <p className="text-destructive text-sm mt-1">{validationErrors.currency_id}</p>}
          </div>

          <div>
            <Label htmlFor="unit_of_measure_id" className="block text-sm font-medium text-foreground mb-1">
              Unit of Measure
            </Label>
            <Select
              onValueChange={(value) => handleSelectChange('unit_of_measure_id', value)}
              value={formData.unit_of_measure_id ? String(formData.unit_of_measure_id) : ''}
              disabled={isDisabled}
              required={!isCreatingComposite} // Required only for standard products
            >
              <SelectTrigger id="unit_of_measure_id">
                <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading units...</span> : 'Select a unit of measure'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nil4" disabled>
                  {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading units...</span> : 'Select a unit of measure'}
                </SelectItem>
                {unitsOfMeasure.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validationErrors.unit_of_measure_id && <p className="text-destructive text-sm mt-1">{validationErrors.unit_of_measure_id}</p>}
          </div>
        </>
      )}

      <div>
        <Label htmlFor="markup_perc_wholesale" className="block text-sm font-medium text-foreground mb-1">
          Wholesale Markup %
        </Label>
        <Input
          id="markup_perc_wholesale"
          type="number"
          step="0.01"
          required
          value={formData.markup_perc_wholesale ?? ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
        {validationErrors.markup_perc_wholesale && <p className="text-destructive text-sm mt-1">{validationErrors.markup_perc_wholesale}</p>}
      </div>

      <div>
        <Label htmlFor="markup_perc_retail" className="block text-sm font-medium text-foreground mb-1">
          Retail Markup %
        </Label>
        <Input
          id="markup_perc_retail"
          type="number"
          step="0.01"
          required
          value={formData.markup_perc_retail ?? ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
        {validationErrors.markup_perc_retail && <p className="text-destructive text-sm mt-1">{validationErrors.markup_perc_retail}</p>}
      </div>

      <div className="flex justify-end gap-2">
        {/* Single Save Button */}
        <Button type="submit" disabled={isDisabled}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;